import type Call from "@/model/interface/Call";
import type CallDisposition from "@/model/interface/CallDisposition";
import type { ContactActiveDeal } from "@/model/interface/Contact";
import type Deal from "@/model/interface/Deal";
import type DealStage from "@/model/interface/DealStage";
import { DealStageCategory } from "@/enum/DealStageCategory";
import type Notification from "@/model/interface/Notification";
import type { RestApiType } from "@/store/rest_api";

export type CachePatch = { undo: () => void };

type CacheLifecycle = {
  dispatch: unknown;
  getState: () => unknown;
};

export async function undoOnError(queryFulfilled: Promise<unknown>, patches: CachePatch[]) {
  try {
    await queryFulfilled;
  } catch {
    patches.forEach((patch) => patch.undo());
  }
}

export function findCachedDisposition(
  api: RestApiType,
  lifecycle: CacheLifecycle,
  dispositionId: string,
): CallDisposition | undefined {
  let found: CallDisposition | undefined;
  for (const cachedArgs of api.util.selectCachedArgsForQuery(lifecycle.getState() as never, "getCallDispositions")) {
    const cached = api.endpoints.getCallDispositions.select(cachedArgs)(lifecycle.getState() as never).data;
    found = cached?.find((disposition) => disposition.id === dispositionId) ?? found;
  }
  return found;
}

export function prependNotificationInAllCaches(
  api: RestApiType,
  lifecycle: CacheLifecycle,
  notification: Notification,
) {
  const dispatchPatch = lifecycle.dispatch as (action: unknown) => CachePatch;
  let alreadyCached = false;
  for (const cachedArgs of api.util.selectCachedArgsForQuery(lifecycle.getState() as never, "getNotifications")) {
    if ((cachedArgs.page ?? 0) !== 0) {
      continue;
    }
    dispatchPatch(
      api.util.updateQueryData("getNotifications", cachedArgs, (draft) => {
        if (draft.items.some((item) => item.id === notification.id)) {
          alreadyCached = true;
          return;
        }
        draft.items.unshift(notification);
        if (draft.size > 0 && draft.items.length > draft.size) {
          draft.items.pop();
        }
        draft.totalElements += 1;
      }),
    );
  }
  if (alreadyCached || notification.status !== "UNREAD") {
    return;
  }
  for (const cachedArgs of api.util.selectCachedArgsForQuery(
    lifecycle.getState() as never,
    "getUnreadNotificationCount",
  )) {
    dispatchPatch(api.util.updateQueryData("getUnreadNotificationCount", cachedArgs, (draft) => draft + 1));
  }
}

export function markNotificationsSeenInAllCaches(
  api: RestApiType,
  lifecycle: CacheLifecycle,
  notificationIds: string[],
): CachePatch[] {
  const dispatchPatch = lifecycle.dispatch as (action: unknown) => CachePatch;
  const patches: CachePatch[] = [];
  const pendingIds = new Set(notificationIds);
  const unreadIds = new Set<string>();
  const cachedListArgs = api.util.selectCachedArgsForQuery(lifecycle.getState() as never, "getNotifications");
  const cachedCountArgs = api.util.selectCachedArgsForQuery(lifecycle.getState() as never, "getUnreadNotificationCount");
  cachedListArgs.forEach((cachedArgs) => {
    patches.push(
      dispatchPatch(
        api.util.updateQueryData("getNotifications", cachedArgs, (draft) => {
          draft.items.forEach((item) => {
            if (pendingIds.has(item.id) && item.status === "UNREAD") {
              item.status = "SEEN";
              unreadIds.add(item.id);
            }
          });
        }),
      ),
    );
  });
  if (unreadIds.size === 0) {
    return patches;
  }
  cachedCountArgs.forEach((cachedArgs) => {
    patches.push(
      dispatchPatch(
        api.util.updateQueryData("getUnreadNotificationCount", cachedArgs, (draft) =>
          Math.max(0, draft - unreadIds.size),
        ),
      ),
    );
  });
  return patches;
}

export function patchCallInAllCaches(
  api: RestApiType,
  lifecycle: CacheLifecycle,
  callId: string,
  applyToCall: (call: Call) => void,
): CachePatch[] {
  const dispatchPatch = lifecycle.dispatch as (action: unknown) => CachePatch;
  const patches: CachePatch[] = [];
  for (const cachedArgs of api.util.selectCachedArgsForQuery(lifecycle.getState() as never, "getCalls")) {
    patches.push(
      dispatchPatch(
        api.util.updateQueryData("getCalls", cachedArgs, (draft) => {
          const target = draft.items.find((item) => item.id === callId);
          if (target) {
            applyToCall(target);
          }
        }),
      ),
    );
  }
  for (const cachedArgs of api.util.selectCachedArgsForQuery(lifecycle.getState() as never, "getCall")) {
    if (cachedArgs === callId) {
      patches.push(
        dispatchPatch(
          api.util.updateQueryData("getCall", cachedArgs, (draft) => {
            applyToCall(draft);
          }),
        ),
      );
    }
  }
  for (const cachedArgs of api.util.selectCachedArgsForQuery(lifecycle.getState() as never, "getCommunicationItems")) {
    patches.push(
      dispatchPatch(
        api.util.updateQueryData("getCommunicationItems", cachedArgs, (draft) => {
          draft.pages.forEach((page) => {
            page.items.forEach((item) => {
              if (item.call?.id === callId) {
                applyToCall(item.call);
              }
            });
          });
        }),
      ),
    );
  }
  return patches;
}

export function findCachedDealStage(
  api: RestApiType,
  lifecycle: CacheLifecycle,
  stageId: string,
): DealStage | undefined {
  let found: DealStage | undefined;
  for (const cachedArgs of api.util.selectCachedArgsForQuery(lifecycle.getState() as never, "getDealStages")) {
    const cached = api.endpoints.getDealStages.select(cachedArgs)(lifecycle.getState() as never).data;
    found = cached?.find((stage) => stage.id === stageId) ?? found;
  }
  return found;
}

export function patchDealInAllCaches(
  api: RestApiType,
  lifecycle: CacheLifecycle,
  dealId: string,
  applyToDeal: (deal: Deal) => void,
): CachePatch[] {
  const dispatchPatch = lifecycle.dispatch as (action: unknown) => CachePatch;
  const patches: CachePatch[] = [];
  for (const cachedArgs of api.util.selectCachedArgsForQuery(lifecycle.getState() as never, "getDeals")) {
    patches.push(
      dispatchPatch(
        api.util.updateQueryData("getDeals", cachedArgs, (draft) => {
          const target = draft.items.find((item) => item.id === dealId);
          if (target) {
            applyToDeal(target);
          }
        }),
      ),
    );
  }
  for (const cachedArgs of api.util.selectCachedArgsForQuery(lifecycle.getState() as never, "getDeal")) {
    if (cachedArgs === dealId) {
      patches.push(
        dispatchPatch(
          api.util.updateQueryData("getDeal", cachedArgs, (draft) => {
            applyToDeal(draft);
          }),
        ),
      );
    }
  }
  return patches;
}

export function reorderDealStagesInAllCaches(
  api: RestApiType,
  lifecycle: CacheLifecycle,
  order: Pick<DealStage, "id" | "category">[],
): CachePatch[] {
  const dispatchPatch = lifecycle.dispatch as (action: unknown) => CachePatch;
  const positionById = new Map(order.map((entry, position) => [entry.id, position]));
  const categoryById = new Map(order.map((entry) => [entry.id, entry.category]));
  const patches: CachePatch[] = [];
  for (const cachedArgs of api.util.selectCachedArgsForQuery(lifecycle.getState() as never, "getDealStages")) {
    patches.push(
      dispatchPatch(
        api.util.updateQueryData("getDealStages", cachedArgs, (draft) => {
          draft.forEach((stage) => {
            const category = categoryById.get(stage.id);
            const position = positionById.get(stage.id);
            if (category && position !== undefined) {
              stage.category = category;
              stage.isClosed = category === DealStageCategory.WON || category === DealStageCategory.LOST;
              stage.sortOrder = position + 1;
            }
          });
          draft.sort(
            (first, second) =>
              (positionById.get(first.id) ?? order.length) - (positionById.get(second.id) ?? order.length),
          );
        }),
      ),
    );
  }
  return patches;
}

export function patchContactActiveDealInAllCaches(
  api: RestApiType,
  lifecycle: CacheLifecycle,
  dealId: string,
  applyToActiveDeal: (activeDeal: ContactActiveDeal) => void,
): CachePatch[] {
  const dispatchPatch = lifecycle.dispatch as (action: unknown) => CachePatch;
  const patches: CachePatch[] = [];
  for (const cachedArgs of api.util.selectCachedArgsForQuery(lifecycle.getState() as never, "getContacts")) {
    patches.push(
      dispatchPatch(
        api.util.updateQueryData("getContacts", cachedArgs, (draft) => {
          draft.items.forEach((contact) => {
            if (contact.activeDeal?.id === dealId) {
              applyToActiveDeal(contact.activeDeal);
            }
          });
        }),
      ),
    );
  }
  for (const cachedArgs of api.util.selectCachedArgsForQuery(lifecycle.getState() as never, "getContact")) {
    patches.push(
      dispatchPatch(
        api.util.updateQueryData("getContact", cachedArgs, (draft) => {
          if (draft.activeDeal?.id === dealId) {
            applyToActiveDeal(draft.activeDeal);
          }
        }),
      ),
    );
  }
  return patches;
}
